import { Link } from "react-router-dom";
import Logo from "./Logo";
import { scrollToSection } from "@/lib/smoothScroll";
import { Caption } from "@/components/ui/typography";
import { ThemeToggleSimple } from "@/components/ui/theme-toggle";
import { openCookieSettings } from "@/components/CookieConsent";
import { Separator } from "@/components/ui/separator";
import { Linkedin, Instagram } from "lucide-react";

const FooterLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link to={to} className="text-muted-foreground hover:text-foreground transition-colors text-sm">
    {children}
  </Link>
);

const FooterAnchor = ({ href, onClick, children }: { href: string; onClick?: (e: React.MouseEvent) => void; children: React.ReactNode }) => (
  <a 
    href={href} 
    onClick={onClick}
    className="text-muted-foreground hover:text-foreground transition-colors text-sm"
  >
    {children}
  </a>
);

const FooterSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-3">
    <h4 className="font-semibold text-foreground text-sm">{title}</h4>
    <div className="flex flex-col gap-2">
      {children}
    </div>
  </div>
);

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const handleScrollLink = (sectionId: string) => (e: React.MouseEvent) => {
    if (window.location.pathname === '/') {
      e.preventDefault();
      scrollToSection(sectionId);
    }
  };

  return (
    <footer className="border-t border-border bg-background">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="max-w-7xl mx-auto">
          {/* Main Footer Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Product */}
            <FooterSection title="Product">
              <FooterAnchor href="/#features" onClick={handleScrollLink('features')}>Features</FooterAnchor>
              <FooterAnchor href="/#how-it-works" onClick={handleScrollLink('how-it-works')}>How It Works</FooterAnchor>
              <FooterLink to="/pricing">Pricing</FooterLink>
              <FooterAnchor href="/#faq" onClick={handleScrollLink('faq')}>FAQ</FooterAnchor>
            </FooterSection>

            {/* Solutions */}
            <FooterSection title="Solutions">
              <FooterLink to="/solutions/hr-teams">For HR Teams</FooterLink>
              <FooterLink to="/solutions/remote-teams">For Remote Teams</FooterLink>
              <FooterLink to="/solutions/enterprise">For Enterprise</FooterLink>
              <FooterLink to="/use-cases/pulse-surveys">Pulse Surveys</FooterLink>
              <FooterLink to="/use-cases/exit-interviews">Exit Interviews</FooterLink>
            </FooterSection>

            {/* Compare */}
            <FooterSection title="Compare">
              <FooterLink to="/compare/surveymonkey-alternative">vs SurveyMonkey</FooterLink>
              <FooterLink to="/compare/culture-amp-alternative">vs Culture Amp</FooterLink>
              <FooterLink to="/compare/lattice-alternative">vs Lattice</FooterLink>
              <FooterLink to="/compare/15five-alternative">vs 15Five</FooterLink>
              <FooterLink to="/compare/qualtrics-alternative">vs Qualtrics</FooterLink>
            </FooterSection>

            {/* Company */}
            <FooterSection title="Company">
              <FooterLink to="/about">About Us</FooterLink>
              <FooterLink to="/contact">Contact</FooterLink>
              <FooterLink to="/how-anonymity-works">How Anonymity Works</FooterLink>
              <FooterLink to="/help">Help Center</FooterLink>
              <FooterLink to="/track">Track Feedback</FooterLink>
            </FooterSection>

            {/* Legal */}
            <FooterSection title="Legal">
              <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
              <FooterLink to="/terms-of-service">Terms of Service</FooterLink>
              <FooterLink to="/refund-policy">Refund Policy</FooterLink>
              <FooterLink to="/cookie-policy">Cookie Policy</FooterLink>
              <button 
                onClick={openCookieSettings}
                className="text-muted-foreground hover:text-foreground transition-colors text-sm text-left"
              >
                Cookie Settings
              </button>
            </FooterSection>
          </div>

          <Separator className="my-8" />

          {/* Bottom Bar */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <Logo size="sm" />
              <Caption>
                © {currentYear} SendFeedback.ai
              </Caption>
            </div>
            <div className="flex items-center gap-4">
              <a 
                href="https://www.linkedin.com/company/sendfeedback/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a 
                href="https://www.instagram.com/sendfeedback.ai" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a 
                href="https://stats.uptimerobot.com/ex9N9VOUU4" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Uptime Status
              </a>
              <ThemeToggleSimple />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
